import React, { useState, useEffect } from "react";
import { 
  Dialog, DialogTitle, DialogContent, 
  Button, TextField, Typography, Box, IconButton, 
  List, ListItem, ListItemText, Chip
} from "@mui/material";
import EventIcon from '@mui/icons-material/Event'; 
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AddTaskIcon from '@mui/icons-material/AddTask';
import LocalShippingIcon from '@mui/icons-material/LocalShipping'; 
import { collection, query, onSnapshot, addDoc, deleteDoc, doc, serverTimestamp, orderBy, where } from "firebase/firestore";
import { db } from "../firebase";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameDay, addMonths, subMonths } from "date-fns";
import { tr } from "date-fns/locale"; 

function AjandaKutusu({ sube, forceOpen, onClose }) {
  const [open, setOpen] = useState(forceOpen || false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  const [manuelNotlar, setManuelNotlar] = useState([]);
  const [teslimatNotlari, setTeslimatNotlari] = useState([]);
  const [tumNotlar, setTumNotlar] = useState([]);
  const [yeniNot, setYeniNot] = useState("");

  const handleClose = () => {
    setOpen(false);
    if (onClose) onClose(); 
  };

  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);

  // --- 1. MANUEL NOTLARI ÇEKME ---
  useEffect(() => {
    if (!sube) return;

    const q = query(collection(db, "subeler", sube, "ajanda"), orderBy("tarih", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        tarihDate: doc.data().tarih?.toDate ? doc.data().tarih.toDate() : new Date(),
        tip: "manuel"
      }));
      setManuelNotlar(data);
    });

    return () => unsubscribe();
  }, [sube]);

  // --- 2. OTOMATİK TESLİMATLARI ÇEKME (YENİ SİSTEM İLE UYUMLU) ---
  useEffect(() => {
    if (!sube) return;

    const q = query(
      collection(db, "subeler", sube, "islemler"), 
      where("teslimatDurumu", "==", "Teslimat Bekliyor")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs
        .map(doc => {
          const d = doc.data();
          
          // Eğer planlanan tarih yoksa takvime koyma
          if (!d.planlananTeslimTarihi) return null;

          // Tarih Stringini (YYYY-MM-DD) Güvenli Şekilde Date Objesine Çevir
          // (Timezone hatasını önlemek için manuel parsing)
          const parts = d.planlananTeslimTarihi.split('-');
          const planDate = new Date(parts[0], parts[1] - 1, parts[2]); 
          planDate.setHours(12, 0, 0, 0); // Saati ortala

          const musteriAdi = d.musteri 
            ? `${d.musteri.isim} ${d.musteri.soyisim || ''}` 
            : (d.firma ? d.firma.firmaIsmi : "Bilinmeyen Müşteri");

          return {
            id: doc.id,
            mesaj: `📦 TESLİMAT: ${d.urun || d.kategori} - ${musteriAdi}`,
            altBilgi: `Tutar: ${d.toplamTutar} TL | Miktar: ${d.adet || d.gram}`,
            tarihDate: planDate,
            tip: "otomatik",
            detay: d 
          };
        })
        .filter(item => item !== null);

      setTeslimatNotlari(data);
    });

    return () => unsubscribe();
  }, [sube]);

  // --- 3. VERİLERİ BİRLEŞTİRME ---
  useEffect(() => {
    setTumNotlar([...manuelNotlar, ...teslimatNotlari]);
  }, [manuelNotlar, teslimatNotlari]);


  // Not Ekleme (Sadece Manuel)
  const handleNotEkle = async () => {
    if (!yeniNot.trim()) return;
    
    const kayitTarihi = new Date(selectedDate);
    kayitTarihi.setHours(12, 0, 0, 0); 

    try {
      await addDoc(collection(db, "subeler", sube, "ajanda"), {
        mesaj: yeniNot,
        tarih: kayitTarihi,
        olusturulma: serverTimestamp()
      });
      setYeniNot("");
    } catch (error) {
      console.error("Not eklenemedi:", error);
    }
  };

  const handleNotSil = async (id) => {
    if (window.confirm("Bu notu silmek istediğinize emin misiniz?")) {
      await deleteDoc(doc(db, "subeler", sube, "ajanda", id));
    }
  };

  const renderHeader = () => {
    return (
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <IconButton onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}><ArrowBackIosIcon fontSize="small"/></IconButton>
        <Typography variant="h6" fontWeight="bold" color="primary">
          {format(currentMonth, "MMMM yyyy", { locale: tr }).toUpperCase()}
        </Typography>
        <IconButton onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}><ArrowForwardIosIcon fontSize="small"/></IconButton>
      </Box>
    );
  };

  const renderDays = () => {
    const days = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
    return (
      <Box sx={{ display: "flex", mb: 1 }}>
        {days.map((day, i) => (
          <Typography key={i} sx={{ width: "14.28%", textAlign: "center", fontWeight: "bold", fontSize: "0.85rem", color: "#555" }}>
            {day}
          </Typography>
        ))}
      </Box>
    );
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 }); 
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const rows = [];
    let days = [];
    let day = startDate;
    let formattedDate = "";

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = format(day, "d");
        const cloneDay = day;
        
        const hasNote = tumNotlar.some(n => n.tarihDate && isSameDay(n.tarihDate, day));
        const hasDelivery = teslimatNotlari.some(n => n.tarihDate && isSameDay(n.tarihDate, day));

        const isSelected = isSameDay(day, selectedDate);
        const isToday = isSameDay(day, new Date());

        days.push(
          <Box
            key={day}
            onClick={() => setSelectedDate(cloneDay)}
            sx={{
              width: "14.28%",
              height: "45px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              borderRadius: "8px",
              bgcolor: isSelected ? "#1976d2" : (isToday ? "#e3f2fd" : "transparent"),
              color: isSelected ? "white" : (!isSameDay(day, monthStart) && day.getMonth() !== currentMonth.getMonth() ? "#ccc" : "black"), 
              border: isToday && !isSelected ? "1px solid #1976d2" : "none",
              position: "relative",
              "&:hover": { bgcolor: isSelected ? "#1565c0" : "#f5f5f5" }
            }}
          >
            <span style={{ fontSize: "0.9rem", fontWeight: isSelected || isToday ? "bold" : "normal" }}>{formattedDate}</span>
            {hasNote && (
              <Box sx={{ 
                width: "6px", 
                height: "6px", 
                borderRadius: "50%", 
                bgcolor: isSelected ? "white" : (hasDelivery ? "#d32f2f" : "orange"), 
                mt: 0.5 
              }} />
            )}
          </Box>
        );
        day = addDays(day, 1);
      }
      rows.push(<Box key={day} sx={{ display: "flex" }}>{days}</Box>);
      days = [];
    }
    return <Box>{rows}</Box>;
  };

  const selectedDayNotes = tumNotlar.filter(n => n.tarihDate && isSameDay(n.tarihDate, selectedDate));

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ bgcolor: '#ed6c02', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventIcon />
          <Typography variant="h6">Mağaza Ajandası</Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, height: '100%' }}>
          
          <Box sx={{ flex: 1, borderRight: { md: '1px solid #eee' }, pr: { md: 2 } }}>
            {renderHeader()}
            {renderDays()}
            {renderCells()}
            
            <Box sx={{ mt: 3, p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{display:'block'}}>
                * <span style={{color:'orange', fontWeight:'bold'}}>Turuncu Nokta:</span> Manuel eklenen notlar.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                * <span style={{color:'#d32f2f', fontWeight:'bold'}}>Kırmızı Nokta:</span> Teslimat bekleyen otomatik kayıtlar.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ borderBottom: '2px solid #1976d2', pb: 1, mb: 2 }}>
              {format(selectedDate, "d MMMM yyyy, EEEE", { locale: tr })}
            </Typography>
            
            <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: '300px', mb: 2 }}>
              {selectedDayNotes.length === 0 ? (
                <Typography color="text.secondary" sx={{ fontStyle: 'italic', mt: 2 }}>
                  Bu tarihe ait not veya teslimat yok.
                </Typography>
              ) : (
                <List>
                  {selectedDayNotes.map((note) => (
                    <React.Fragment key={note.id}>
                      <ListItem 
                        alignItems="flex-start"
                        secondaryAction={
                          note.tip === "manuel" ? (
                            <IconButton edge="end" color="error" onClick={() => handleNotSil(note.id)}>
                              <DeleteIcon />
                            </IconButton>
                          ) : (
                            <Chip 
                                icon={<LocalShippingIcon style={{fontSize:14, color:'white'}} />} 
                                label="TESLİMAT" 
                                size="small" 
                                sx={{ bgcolor:'#d32f2f', color:'white', fontSize:'10px', fontWeight:'bold' }} 
                            />
                          )
                        }
                        sx={{ 
                          bgcolor: note.tip === "otomatik" ? '#ffebee' : '#fff3e0', 
                          borderRadius: 1, 
                          mb: 1,
                          borderLeft: note.tip === "otomatik" ? '4px solid #d32f2f' : '4px solid orange',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                        }}
                      >
                        <ListItemText 
                          primary={
                            <Typography variant="body1" sx={{ fontWeight: note.tip === 'otomatik' ? 'bold' : 'normal', color: '#333' }}>
                              {note.mesaj}
                            </Typography>
                          } 
                          secondary={
                              note.tip === "otomatik" && (
                                  <Typography variant="caption" sx={{ display:'block', mt:0.5, color:'#555' }}>
                                      {note.altBilgi}
                                  </Typography>
                              )
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>

            <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid #eee' }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Yeni hatırlatma ekle..."
                value={yeniNot}
                onChange={(e) => setYeniNot(e.target.value)}
                multiline
                maxRows={3}
              />
              <Button 
                fullWidth 
                variant="contained" 
                color="warning" 
                startIcon={<AddTaskIcon />} 
                sx={{ mt: 1 }}
                onClick={handleNotEkle}
              >
                EKLE
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default AjandaKutusu;